import { createFileRoute, Link } from "@tanstack/react-router"
import { Calendar, Dumbbell, Timer } from "lucide-react"
import { DateTime } from "luxon"
import { useEffect, useState } from "react"
import { z } from "zod"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import {
  calculateExerciseWeight,
  calculateTotalWeight,
  displayDuration,
  type TExercise,
  type TWorkout,
} from "../utils/workoutUtils"

export const Route = createFileRoute("/workoutShare")({
  component: WorkoutShare,
  validateSearch: z.object({ token: z.string().optional() }),
})

type PreviewData = {
  sharerName: string
  workout: TWorkout
}

type UIState = "loading" | "preview" | "invalid"

function WorkoutShare() {
  const { token } = Route.useSearch()
  const [uiState, setUiState] = useState<UIState>("loading")
  const [preview, setPreview] = useState<PreviewData | null>(null)

  useEffect(() => {
    if (!token) {
      setUiState("invalid")
      return
    }

    const apiUrl = import.meta.env.VITE_API_URL as string
    fetch(`${apiUrl}/share/workout/${token}`)
      .then(async (res) => {
        if (!res.ok) {
          setUiState("invalid")
          return
        }
        const data = (await res.json()) as PreviewData
        setPreview(data)
        setUiState("preview")
      })
      .catch(() => setUiState("invalid"))
  }, [token])

  if (uiState === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center text-muted-foreground">Loading...</CardContent>
        </Card>
      </div>
    )
  }

  if (uiState === "invalid") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>Invalid or Expired Link</CardTitle>
            <CardDescription>This share link is invalid, has been revoked, or has expired.</CardDescription>
          </CardHeader>
          <CardFooter className="justify-center">
            <Link to="/login">
              <Button>Sign In</Button>
            </Link>
          </CardFooter>
        </Card>
      </div>
    )
  }

  if (!preview) {
    return null
  }

  const { workout, sharerName } = preview

  return (
    <div className="flex min-h-screen justify-center bg-background p-4">
      <div className="w-full max-w-md py-8">
        <div className="mb-4 flex justify-center">
          <img src="/openiron.png" alt="OpenIron Logo" className="h-12 w-12" />
        </div>
        <Card>
          <CardHeader>
            <div className="text-center">
              <CardTitle>{workout.name}</CardTitle>
              <CardDescription>{sharerName} shared this completed workout.</CardDescription>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {workout.color && <div className="h-1.5 w-full rounded-full" style={{ backgroundColor: workout.color }} />}
            <div className="flex flex-wrap justify-center gap-x-4 text-muted-foreground text-sm">
              <span className="flex items-center">
                <Calendar size={14} className="mr-1" />
                {DateTime.fromISO(workout.dateScheduled).toLocaleString(DateTime.DATE_MED)}
              </span>
              <span className="flex items-center">
                <Dumbbell size={14} className="mr-1" />
                {workout.exercises.length} exercises
              </span>
              {workout.dateStarted && workout.dateCompleted && (
                <span className="flex items-center">
                  <Timer size={14} className="mr-1" />
                  {displayDuration(workout.dateStarted, workout.dateCompleted)}
                </span>
              )}
              <span className="font-medium text-workout-secondary">
                {calculateTotalWeight(workout).toLocaleString()} lbs total
              </span>
            </div>

            <div className="space-y-3">
              {workout.exercises.map((exercise: TExercise) => (
                <div key={exercise.exerciseId ?? exercise.name} className="border-border border-t pt-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">{exercise.name}</h4>
                    <span className="font-medium text-sm text-workout-secondary">
                      {calculateExerciseWeight(exercise).toLocaleString()} lbs total
                    </span>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2 text-muted-foreground text-sm sm:grid-cols-3">
                    {exercise.sets
                      .filter((x) => x.isCompleted)
                      .map((set, index) => (
                        <div key={set.setId} className="rounded-md bg-muted/30 px-3 py-2">
                          Set {index + 1}: {set.weight} lbs × {set.reps} reps
                        </div>
                      ))}
                  </div>
                </div>
              ))}
            </div>

            {workout.notes && (
              <div className="border-border border-t pt-3">
                <h4 className="font-medium">Notes</h4>
                <p className="mt-1 text-muted-foreground">{workout.notes}</p>
              </div>
            )}
          </CardContent>
          <CardFooter className="justify-center">
            <Link to="/">
              <Button variant="outline">Back to Home</Button>
            </Link>
          </CardFooter>
        </Card>
      </div>
    </div>
  )
}
